import Graphs from '../Graphs';
import { scaleLinear, scaleBand, scaleOrdinal } from 'd3-scale';
import { max, sum} from 'd3-array';
import { select, mouse, event, datum } from 'd3-selection';
import { transition } from 'd3-transition';
import { axisLeft, axisBottom } from 'd3-axis';
import { stack, stackOrderNone, stackOffsetNone, pie, arc } from 'd3-shape';
import { format } from 'd3-format';
import { timeFormat } from 'd3-time-format';

import './StackedGraph.css';

export default class StackedGraph extends Graphs {
  renderChart = () => {
    const node = this.node;
    const keys = this.props.data.length ? Object.keys(this.props.data[0]).filter(key => key !== 'cat') : [];
    const padding = 20;
    const thisComp = this;
    const stacked = stack()
      .keys(keys)
      .value((d, key) => d[key].value)
      .order(stackOrderNone)
      .offset(stackOffsetNone);

    global.transition = transition;
    this.setState({className: 'StackedGraph'});

    const g = select(node)
      .append("g")
      .attr("transform", `translate(${padding}, ${padding})`);

    select(node)
      .on('click', function() {
          drillMenu.transition().style('display', 'none')
          thisComp.setState({ selectedSeries: undefined })
      })

    const zScale = scaleOrdinal(this.props.colorScheme)
      .domain(keys);

    const yScale = scaleLinear()
			.domain([0,
				max(this.props.data, d => sum(Object.values(d).map(item => item.value)))
			])
			.rangeRound([this.props.size[1] - padding * 2, padding]);

    const xScale = scaleBand()
			.rangeRound([padding, this.props.size[0] - padding * 2])
      .padding(0.5)
      .domain(this.props.data.map(item => item.cat));

    g
      .selectAll('.series')
      .data(stacked(this.props.data))
      .enter()
      .append('g')
      .attr("class", "series")
      .attr("fill", (d) => zScale(d.key))
      .selectAll("rect")
      .data(d => d)
      .enter()
      .append("rect")
      .style('cursor', thisComp.props.drillOptions ? 'pointer' : null)
      .on('mouseover', function(d,i) {
        tooltip.style("display", null);
        thisComp.setState({currMouse: select(this).attr('fill')})
        return select(this)
        .attr('fill', 'teal');
      })
      .on('mouseout', function(d, i) {
        tooltip.style("display", "none");
        return select(this)
        .transition("fillColor")
        .duration(200)
        .attr('fill', thisComp.state.currMouse);
      })
      .on("mousemove", function(d, i) {
        var xPosition = mouse(this)[0] - 5;
        var yPosition = mouse(this)[1] - 5;
        tooltip.attr("transform", `translate(${xPosition}, ${yPosition})`);
        tooltip
          .select("text")
          .text(`${d.data.cat}: ${format(".3s")(d[1]-d[0])}`);
        tooltip
          .select('rect')
          .attr('width', () => tooltip.select('text').node().getComputedTextLength()+20)
          .attr('transform', `translate(${-10-tooltip.select('text').node().getComputedTextLength()/16}, 0)`)
      })
      .on('click', function(d, i) {
         // get the index of the series that the rect is in, then use that to find the serieslabel
        const seriesLabel = d.data[keys[select(node).selectAll('.series').nodes().indexOf(this.parentNode)]].label
        event.stopPropagation();
        var xPosition = mouse(this)[0] - 5;
        var yPosition = mouse(this)[1] - 5;
        tooltip.style("display", "none");
        thisComp.setState({ selectedSeries: seriesLabel, selectedDate: d.data.cat })
        drillMenu
          .transition("move")
          .style('display', null)
          .attr("transform", `translate(${xPosition+25}, ${yPosition+25})`)
      })
      .attr("x", d => xScale(d.data.cat))
      .attr("y", d => yScale(d[1]))
      .attr("width", xScale.bandwidth())
      .transition("entry")
      .delay((d, i) => i*100)
      .attr("height", d => yScale(d[0]) - yScale(d[1]));

    g
      .selectAll('.totalText')
      .data(this.props.data)
      .enter()
      .append('text')
      .attr('class', 'totalText')
      .attr("x", d => xScale(d.cat) + xScale.bandwidth() / 2)
      .attr("y", d => yScale(sum(Object.values(d).map(item => item.value))) - padding / 3)
      .style('text-anchor', 'middle')
      .style('font-size', '0.5em')
      .text(d => format(".3s")(sum(Object.values(d).map(item => item.value))));

    g
      .append('g')
      .attr('class', 'axis axis--x')
      .attr('transform', `translate(0, ${this.props.size[1] - padding*2})`)
      .call(axisBottom(xScale).tickValues(xScale.domain().filter((d, i, arr) => (!(i%7) || arr.length < 15))))

    g
      .append('g')
      .attr('class', 'axis axis--y')
      .attr('transform', `translate(${padding}, 0)`)
      .call(axisLeft(yScale).tickFormat(format(".3s")));

    const tooltip = select(node).append("g")
      .attr("class", "tooltip")
      .style("display", "none");

    tooltip.append("rect")
      .attr("width", '20')
      .attr("height", 20)
      .attr("fill", "#333");

    tooltip.append("text")
      .attr("x", 30)
      .attr("dy", "1.2em")
      .style("text-anchor", "middle")
      .attr("font-size", "0.5em")
      .attr("fill", "#fff")

    let drillMenu = select(node)
      .append("g")
      .attr("class", "drillMenu")
      .style("display", "none");

    const arced = arc()
      .outerRadius(50)
      .innerRadius(20);

    const pied = pie()
      .sort(null)
      .padAngle(.01)
      .value(d => d);

    const pieDrill = drillMenu
      .selectAll('.arc')
      .data(pied(thisComp.props.drillOptions ? thisComp.props.drillOptions.map(() => 1) : []))
      .enter()
      .append('g')
      .attr('class', 'arc');

    pieDrill
      .append("path")
      .on('mouseover', function () { return select(this).style('fill', 'teal').style('opacity', 1) })
      .on('mouseout', function () { return select(this).transition("fillColor").style('fill', 'teal').style('opacity', 0.7) })
      .on('click', function(d, i) { return thisComp.props.drillData ? thisComp.props.drillData[i].onClick({
        date: thisComp.state.selectedDate,
        series: thisComp.state.selectedSeries,
        widgetType: thisComp.props.drillData[i].widgetType,
        widgetTitle: thisComp.props.drillData[i].widgetTitle,
        param: thisComp.props.drillData[i].param,
        filters: thisComp.props.filters
      }) : null
      })
      .style('cursor', 'pointer')
      .attr("d", arced)
      .style("fill", 'teal')
      .style('opacity', 0)
      .transition("entry")
      .style('opacity', 0.7)

    pieDrill
      .append("text")
      .attr("transform", function(d) {
        d.innerRadius = 20;
        d.outerRadius = 50;
        return "translate(" + arced.centroid(d) + ")";
      })
      .on('click', function(d, i) { return thisComp.props.drillData ? thisComp.props.drillData[i].onClick({
        date: thisComp.state.selectedDate,
        series: thisComp.state.selectedSeries,
        widgetType: thisComp.props.drillData[i].widgetType,
        widgetTitle: thisComp.props.drillData[i].widgetTitle,
        param: thisComp.props.drillData[i].param,
        filters: thisComp.props.filters
      }) : null
      })
      .style("font-size", '0.5em')
      .style("fill", 'white')
      .attr("text-anchor", "middle")
      .text(function(d, i) { return thisComp.props.drillOptions[i]; });

  }
}



// WEBPACK FOOTER //
// ./src/components/Graphs/StackedGraphComponent/StackedGraph.js